/**
 * DefencIQ — Hybrid DISP Readiness Report PDF Renderer (Phase 2)
 *
 * Renders the 9 hybrid_report sections into the existing Technical PDF.
 * Pure additive module — does NOT mutate scoring, CSQ, or other report data.
 *
 * Constraint: jsPDF Helvetica supports Windows-1252 only — keep ASCII.
 */

import autoTable from 'jspdf-autotable';
import { SECTION_CONFIG } from '@/config/domain-weights';

// Reuse the COLORS palette from pdf-generator.js (passed via ctx)
const NEW_PAGE_THRESHOLD = 25;

// Clean a raw question prompt into an outcome-oriented remediation statement.
// Strips leading interrogative phrases and trailing punctuation so the roadmap
// reads like consultant guidance, not a list of assessment questions.
// Handles multi-word interrogatives (How frequently, How would you describe,
// Is there, Does your organisation have, etc.) and rescues weak fragments
// (starts with "there", "your", "any", "a/an/the") by prefixing "Confirm ".
export const cleanActionText = (raw) => {
  if (!raw) return 'Remediate identified control gap';
  let t = String(raw).trim();
  const strippers = [
    /^Address:\s*/i,
    // Complex "How X are/is..." patterns
    /^How\s+(frequently|often|structured|regularly|comprehensive|mature|robust|effective)\s+(are|is|do|does|have|has)\s+/i,
    /^How\s+would\s+you\s+describe\s+(your|the)\s+/i,
    /^How\s+(are|is|do|does|have|has|often|frequently)\s+/i,
    /^How\s+/i,
    // "When/Where/Why + aux"
    /^When\s+(was|is|are|were|will|does|do|has|have)\s+/i,
    /^Where\s+(is|are|does|do|can|has|have)\s+/i,
    /^Why\s+(is|are|does|do)\s+/i,
    // "What + aux"
    /^What\s+(is|are|does|do)\s+(of|in|the|a|an|any)?\s*/i,
    /^What\s+(kind|type|frequency|proportion|percentage|level|method|process|controls|evidence|scope)\s+of\s+/i,
    /^What\s+/i,
    // "Is/Are there"
    /^Is\s+there\s+(a|an|any|the)?\s*/i,
    /^Are\s+there\s+(a|an|any|the)?\s*/i,
    // "Does/Do your organisation + verb"
    /^Does\s+(your\s+organisation|the\s+organisation|the\s+business|the\s+company|your\s+company|your)\s+(have|use|maintain|hold|conduct|apply|require|possess|operate|manage|provide|ensure|require)\s+/i,
    /^Do\s+(you|your\s+organisation|your\s+staff|any\s+of\s+your)\s+(have|use|maintain|hold|conduct|apply|possess|operate)\s+/i,
    /^Does\s+(your|the)\s+/i,
    /^Do\s+(your|any|you)\s+/i,
    /^Has\s+(your\s+organisation|the\s+organisation|the\s+business|the\s+company|your)\s+/i,
    /^Have\s+(you|your|the|any)\s+/i,
    // Bare interrogatives
    /^(Is|Are|Do|Does|Has|Have|Can|Will|Would|Should)\s+/i,
  ];
  strippers.forEach(re => { t = t.replace(re, ''); });
  t = t.replace(/\?+\s*$/, '').trim();

  // Rescue weak leading fragments left after stripping
  if (t.length > 0 && /^(there|your|any|a|an|the|based|doing|with|for|in|on|at|of|out|outside|and|or|you|it)\b/i.test(t)) {
    t = 'Confirm ' + t.charAt(0).toLowerCase() + t.slice(1);
  }
  if (t.length > 0) t = t.charAt(0).toUpperCase() + t.slice(1);
  return t || 'Remediate identified control gap';
};

const domainLabelFromSectionId = (sectionId) =>
  SECTION_CONFIG[sectionId]?.title || (sectionId || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ---------- shared helpers ----------
const sectionHeader = (doc, ctx, title, subtitle) => {
  const { COLORS, margin, contentWidth } = ctx;
  if (ctx.yPos + NEW_PAGE_THRESHOLD > ctx.pageHeight - margin) {
    doc.addPage();
    ctx.yPos = margin;
  }
  doc.setFillColor(...COLORS.primary);
  doc.rect(margin, ctx.yPos, contentWidth, 10, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), margin + 5, ctx.yPos + 7);
  ctx.yPos += 14;
  if (subtitle) {
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(subtitle, margin, ctx.yPos);
    ctx.yPos += 6;
  }
};

const ensureSpace = (doc, ctx, required = 30) => {
  if (ctx.yPos + required > ctx.pageHeight - ctx.margin) {
    doc.addPage();
    ctx.yPos = ctx.margin;
  }
};

const writeParagraph = (doc, ctx, text, opts = {}) => {
  const { COLORS, margin, contentWidth } = ctx;
  const { color = COLORS.secondary, size = 9, italic = false, bold = false } = opts;
  doc.setTextColor(...color);
  doc.setFontSize(size);
  doc.setFont('helvetica', bold ? 'bold' : italic ? 'italic' : 'normal');
  const lines = doc.splitTextToSize(text || '', contentWidth - 4);
  lines.forEach(line => {
    ensureSpace(doc, ctx, 6);
    doc.text(line, margin + 2, ctx.yPos);
    ctx.yPos += size * 0.5;
  });
  ctx.yPos += 2;
};

const writeLabel = (doc, ctx, label) => {
  const { COLORS, margin } = ctx;
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  ensureSpace(doc, ctx, 8);
  doc.text(label, margin, ctx.yPos);
  ctx.yPos += 6;
};

const writeBullet = (doc, ctx, text, color) => {
  const { COLORS, margin, contentWidth } = ctx;
  const dotColor = color || COLORS.secondary;
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(text || '', contentWidth - 14);
  lines.forEach((line, i) => {
    ensureSpace(doc, ctx, 6);
    if (i === 0) {
      doc.setFillColor(...dotColor);
      doc.circle(margin + 4, ctx.yPos - 1.2, 1.2, 'F');
    }
    doc.text(line, margin + 9, ctx.yPos);
    ctx.yPos += 5;
  });
};

const drawBadge = (doc, ctx, text, fillColor, x, y) => {
  doc.setFillColor(...fillColor);
  const w = doc.getTextWidth(text) + 8;
  doc.roundedRect(x, y, w, 6, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(text, x + 4, y + 4.2);
  return w;
};

// Status -> color
const statusColor = (COLORS, status) => {
  const map = {
    Pass: COLORS.success,
    Partial: COLORS.warning,
    Fail: COLORS.danger,
    Unanswered: COLORS.secondary,
    Ready: COLORS.success,
    'Conditionally Ready': COLORS.warning,
    'Not Ready': COLORS.danger,
    'Ready for Submission': COLORS.success,
    'Submit with Conditions': COLORS.warning,
    'Do Not Submit': COLORS.danger,
    Critical: COLORS.critical,
    High: COLORS.danger,
    Medium: COLORS.warning,
    Low: COLORS.secondary,
  };
  return map[status] || COLORS.secondary;
};

// Inverted scale: maturity / confidence (Low = bad, High = good)
const maturityColor = (COLORS, level) => {
  const map = {
    Low: COLORS.danger,
    Medium: COLORS.warning,
    High: COLORS.success,
  };
  return map[level] || COLORS.secondary;
};

// ============================================================================
// 1. EXECUTIVE SNAPSHOT
// ============================================================================
const renderExecutiveSnapshot = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Executive Snapshot', 'Board-ready DISP readiness summary');

  // Top KPI row: 4 boxes
  ensureSpace(doc, ctx, 35);
  const cardW = (contentWidth - 9) / 4;
  const cardH = 24;
  const cards = [
    { label: 'SUBMISSION STATUS', value: data.submission_status, color: statusColor(COLORS, data.submission_status) },
    { label: 'CONFIDENCE', value: data.submission_confidence, color: maturityColor(COLORS, data.submission_confidence) },
    { label: 'WEIGHTED ML2', value: `${data.weighted_ml2_score}%`, color: COLORS.primary, sub: data.score_band },
    { label: 'TIME TO READY', value: data.estimated_time_to_readiness, color: COLORS.primary },
  ];
  cards.forEach((c, i) => {
    const x = margin + i * (cardW + 3);
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(x, ctx.yPos, cardW, cardH, 2, 2, 'F');
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(x, ctx.yPos, cardW, cardH, 2, 2, 'S');
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(c.label, x + 3, ctx.yPos + 5);
    doc.setTextColor(...c.color);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    const valLines = doc.splitTextToSize(c.value || '', cardW - 6);
    doc.text(valLines[0] || '', x + 3, ctx.yPos + 14);
    if (c.sub) {
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(c.sub, x + 3, ctx.yPos + 20);
    }
  });
  ctx.yPos += cardH + 6;

  // Top business risks
  if (data.top_business_risks?.length > 0) {
    writeLabel(doc, ctx, 'Top Business Risks');
    data.top_business_risks.forEach(r => {
      writeBullet(doc, ctx, `${r.title} - ${r.description}`, COLORS.danger);
    });
    ctx.yPos += 2;
  }

  // Narrative box
  ensureSpace(doc, ctx, 30);
  const narrLines = doc.splitTextToSize(data.narrative || '', contentWidth - 8);
  const boxH = narrLines.length * 4.5 + 10;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, ctx.yPos, contentWidth, boxH, 2, 2, 'F');
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('EXECUTIVE NARRATIVE', margin + 4, ctx.yPos + 5);
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  narrLines.forEach((line, i) => {
    doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5);
  });
  ctx.yPos += boxH + 6;
};

// ============================================================================
// 1.5 COMMERCIAL IMPACT & SUBMISSION RISK
// ============================================================================
const renderCommercialImpact = (doc, ctx, data) => {
  if (!data) return;
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Commercial Impact & Submission Risk', 'Business consequences of the current control posture');

  ensureSpace(doc, ctx, 32);
  const cardW = (contentWidth - 9) / 4;
  const cardH = 22;
  const cards = [
    { label: 'CONTRACT DELAY', value: data.estimated_contract_delay, color: COLORS.primary },
    { label: 'CSQ REJECTION', value: data.csq_rejection_likelihood, color: statusColor(COLORS, data.csq_rejection_likelihood) },
    { label: 'RESUBMISSION RISK', value: data.resubmission_risk, color: statusColor(COLORS, data.resubmission_risk) },
    { label: 'DELIVERY RISK', value: data.delivery_risk, color: statusColor(COLORS, data.delivery_risk) },
  ];
  cards.forEach((c, i) => {
    const x = margin + i * (cardW + 3);
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(x, ctx.yPos, cardW, cardH, 2, 2, 'F');
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(x, ctx.yPos, cardW, cardH, 2, 2, 'S');
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(c.label, x + 3, ctx.yPos + 5);
    doc.setTextColor(...c.color);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    const lines = doc.splitTextToSize(c.value || '', cardW - 6);
    doc.text(lines[0] || '', x + 3, ctx.yPos + 14);
  });
  ctx.yPos += cardH + 6;

  // Narrative box
  ensureSpace(doc, ctx, 30);
  const narrLines = doc.splitTextToSize(data.narrative || '', contentWidth - 8);
  const boxH = narrLines.length * 4.5 + 10;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, ctx.yPos, contentWidth, boxH, 2, 2, 'F');
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('COMMERCIAL NARRATIVE', margin + 4, ctx.yPos + 5);
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  narrLines.forEach((line, i) => doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5));
  ctx.yPos += boxH + 6;
};

// ============================================================================
// 2. HOW TO READ THIS REPORT
// ============================================================================
const renderHowToRead = (doc, ctx, data) => {
  sectionHeader(doc, ctx, 'How to Read This Report', 'Concepts, terms, and maturity layers');
  data.sections.forEach(s => {
    writeLabel(doc, ctx, s.heading);
    writeParagraph(doc, ctx, s.body);
  });
};

// ============================================================================
// 3. DECISION VIEW
// ============================================================================
const renderDecisionView = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'DISP Submission Decision', 'Domain status, recommendation, and required actions');

  // Domain status table
  ensureSpace(doc, ctx, 50);
  autoTable(doc, {
    startY: ctx.yPos,
    head: [['Domain', 'Status', 'Score', 'Gating Failures']],
    body: data.domain_status_table.map(d => [d.domain, d.status, `${d.percent}%`, String(d.gating_failures)]),
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: COLORS.primary },
    columnStyles: {
      1: { fontStyle: 'bold' },
    },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.column.index === 1) {
        const status = hookData.cell.text[0];
        const c = statusColor(COLORS, status);
        hookData.cell.styles.textColor = c;
      }
    },
    margin: { left: margin, right: margin },
  });
  ctx.yPos = doc.lastAutoTable.finalY + 6;

  // Final recommendation badge + reason
  ensureSpace(doc, ctx, 30);
  const recColor = statusColor(COLORS, data.final_recommendation);
  doc.setFillColor(...recColor);
  doc.roundedRect(margin, ctx.yPos, contentWidth, 12, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`FINAL RECOMMENDATION: ${data.final_recommendation.toUpperCase()}`, margin + 5, ctx.yPos + 8);
  ctx.yPos += 16;
  writeParagraph(doc, ctx, data.final_recommendation_reason);

  // Required before submission
  if (data.required_before_submission?.length > 0) {
    writeLabel(doc, ctx, 'Required Before Submission');
    data.required_before_submission.forEach((a, i) => {
      const text = `${i + 1}. ${a.action}  [${(a.priority || '').toUpperCase()} - ${a.domain}]`;
      writeBullet(doc, ctx, text, COLORS.danger);
    });
    ctx.yPos += 2;
  }
};

// ============================================================================
// 4. DOMAIN ENHANCEMENTS (per super-domain)
// ============================================================================
const renderDomainEnhancements = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Per-Domain Risk & Priority', 'Submission risk, control priority, and audit-ready evidence');

  const order = ['governance', 'personnel_security', 'physical_security', 'ict_security'];
  order.forEach(superId => {
    const d = data[superId];
    if (!d) return;
    ensureSpace(doc, ctx, 30);
    // Domain header
    writeLabel(doc, ctx, `${d.domain_label}  -  Score: ${d.score_percent}%  -  Gating failures: ${d.gating_failures}`);

    // Risk badges row
    let bx = margin;
    if (d.risk_to_submission.is_gating_failure) {
      bx += drawBadge(doc, ctx, 'GATING FAILURE', COLORS.danger, bx, ctx.yPos) + 3;
    }
    if (d.risk_to_submission.will_fail_csq) {
      bx += drawBadge(doc, ctx, 'CSQ WILL FAIL', COLORS.critical || COLORS.danger, bx, ctx.yPos) + 3;
    } else if (d.risk_to_submission.defence_will_clarify) {
      bx += drawBadge(doc, ctx, 'CLARIFICATION LIKELY', COLORS.warning, bx, ctx.yPos) + 3;
    }
    if (bx > margin) ctx.yPos += 9;

    // Risk narrative
    writeParagraph(doc, ctx, d.risk_to_submission.narrative, { italic: true });

    // Top risks (max 3)
    if (d.top_risks?.length > 0) {
      writeLabel(doc, ctx, 'Top Risks in This Domain');
      d.top_risks.forEach(r => writeBullet(doc, ctx, r, COLORS.danger));
      ctx.yPos += 1;
    }

    // Priority controls (max 3)
    if (d.priority_controls?.length > 0) {
      writeLabel(doc, ctx, 'Priority Controls to Address');
      d.priority_controls.forEach(c => {
        writeBullet(doc, ctx, `${c.control}  [weight ${c.weight} - ${c.status}]`, COLORS.warning);
      });
      ctx.yPos += 1;
    }

    // Consultant insight
    if (d.consultant_insight) {
      ensureSpace(doc, ctx, 18);
      const lines = doc.splitTextToSize(d.consultant_insight, contentWidth - 10);
      const h = lines.length * 4.5 + 10;
      doc.setFillColor(...COLORS.light);
      doc.roundedRect(margin, ctx.yPos, contentWidth, h, 2, 2, 'F');
      doc.setDrawColor(...COLORS.primary);
      doc.setLineWidth(1.5);
      doc.line(margin, ctx.yPos, margin, ctx.yPos + h);
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('CONSULTANT INSIGHT', margin + 4, ctx.yPos + 5);
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      lines.forEach((line, i) => doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5));
      ctx.yPos += h + 4;
    }

    // Control priority table
    if (d.control_priority_table?.length > 0) {
      ensureSpace(doc, ctx, 30);
      autoTable(doc, {
        startY: ctx.yPos,
        head: [['Control', 'Status', 'Weight', 'Risk']],
        body: d.control_priority_table.slice(0, 10).map(c => [
          c.control + (c.is_gating ? ' [gating]' : ''),
          c.status,
          String(c.weight),
          c.risk_level,
        ]),
        theme: 'striped',
        headStyles: { fillColor: COLORS.secondary, textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: COLORS.primary },
        columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 22 }, 2: { cellWidth: 18 }, 3: { cellWidth: 22 } },
        didParseCell: (hookData) => {
          if (hookData.section === 'body' && (hookData.column.index === 1 || hookData.column.index === 3)) {
            const t = hookData.cell.text[0];
            hookData.cell.styles.textColor = statusColor(COLORS, t);
            hookData.cell.styles.fontStyle = 'bold';
          }
        },
        margin: { left: margin, right: margin },
      });
      ctx.yPos = doc.lastAutoTable.finalY + 4;
    }

    // Evidence required
    if (d.evidence_required?.length > 0) {
      writeLabel(doc, ctx, 'Evidence Required');
      d.evidence_required.forEach(e => writeBullet(doc, ctx, e, COLORS.secondary));
      ctx.yPos += 2;
    }
  });
};

// ============================================================================
// 5. GATING CONTROL ASSESSMENT
// ============================================================================
const renderGatingAnalysis = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Gating Control Assessment', 'Controls that determine submission eligibility');

  // Summary row
  ensureSpace(doc, ctx, 18);
  const cellW = (contentWidth - 6) / 3;
  const cells = [
    { label: 'PASS', value: data.summary.pass, color: COLORS.success },
    { label: 'PARTIAL', value: data.summary.partial, color: COLORS.warning },
    { label: 'FAIL', value: data.summary.fail, color: COLORS.danger },
  ];
  cells.forEach((c, i) => {
    const x = margin + i * (cellW + 3);
    doc.setFillColor(...COLORS.light);
    doc.roundedRect(x, ctx.yPos, cellW, 14, 2, 2, 'F');
    doc.setTextColor(...c.color);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(String(c.value), x + 4, ctx.yPos + 9);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(c.label, x + cellW - 18, ctx.yPos + 9);
  });
  ctx.yPos += 18;

  // Gating table
  ensureSpace(doc, ctx, 30);
  autoTable(doc, {
    startY: ctx.yPos,
    head: [['Control', 'Status', 'Impact']],
    body: data.gating_table.map(c => [cleanActionText(c.control), c.status, c.impact]),
    theme: 'grid',
    headStyles: { fillColor: COLORS.primary, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 8, textColor: COLORS.primary },
    columnStyles: { 0: { cellWidth: 85 }, 1: { cellWidth: 22 }, 2: { cellWidth: 55 } },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.column.index === 1) {
        const t = hookData.cell.text[0];
        hookData.cell.styles.textColor = statusColor(COLORS, t);
        hookData.cell.styles.fontStyle = 'bold';
      }
    },
    margin: { left: margin, right: margin },
  });
  ctx.yPos = doc.lastAutoTable.finalY + 6;

  // Insight box
  ensureSpace(doc, ctx, 30);
  const insightLines = doc.splitTextToSize(data.insight || '', contentWidth - 8);
  const boxH = insightLines.length * 4.5 + 10;
  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(margin, ctx.yPos, contentWidth, boxH, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('GATING INSIGHT', margin + 4, ctx.yPos + 5);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  insightLines.forEach((line, i) => {
    doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5);
  });
  ctx.yPos += boxH + 6;
};

// ============================================================================
// 6. EVIDENCE READINESS & DEFENSIBILITY
// ============================================================================
const renderEvidenceReadiness = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Evidence Readiness & Defensibility', 'Maturity, findings, and required improvements');

  // Maturity badge inline
  ensureSpace(doc, ctx, 12);
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Evidence Maturity:', margin, ctx.yPos);
  drawBadge(doc, ctx, data.evidence_maturity_score, maturityColor(COLORS, data.evidence_maturity_score), margin + 35, ctx.yPos - 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(8);
  doc.text(
    `(Evidence x${data.evidence_multiplier.toFixed(2)} - Policy x${data.policy_enforcement_multiplier.toFixed(2)})`,
    margin + 65, ctx.yPos
  );
  ctx.yPos += 8;

  // Findings
  if (data.findings?.length > 0) {
    writeLabel(doc, ctx, 'Findings');
    data.findings.forEach(f => writeBullet(doc, ctx, f, COLORS.warning));
    ctx.yPos += 2;
  }

  // Required improvements
  if (data.required_improvements?.length > 0) {
    writeLabel(doc, ctx, 'Required Improvements');
    data.required_improvements.forEach((r, i) => writeBullet(doc, ctx, `${i + 1}. ${r}`, COLORS.accent));
    ctx.yPos += 2;
  }

  // Defence Lens
  ensureSpace(doc, ctx, 22);
  const lensLines = doc.splitTextToSize(data.defence_lens || '', contentWidth - 10);
  const lensH = lensLines.length * 4.5 + 10;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, ctx.yPos, contentWidth, lensH, 2, 2, 'F');
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(1.5);
  doc.line(margin, ctx.yPos, margin, ctx.yPos + lensH);
  doc.setTextColor(...COLORS.secondary);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('DEFENCE LENS', margin + 4, ctx.yPos + 5);
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  lensLines.forEach((line, i) => {
    doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5);
  });
  ctx.yPos += lensH + 6;
};

// ============================================================================
// 7. CSQ ENHANCEMENTS
// ============================================================================
const renderCsqEnhancements = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  if (!data || data.length === 0) return;
  sectionHeader(doc, ctx, 'CSQ Submission Risk & Numbered Path', 'Per-category risk plus numbered minimum viable steps');

  data.forEach(b => {
    ensureSpace(doc, ctx, 30);
    // Category header line
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const headerText = `${b.csq_domain} - ${b.csq_category}`;
    doc.text(headerText, margin, ctx.yPos);
    // Risk badge to the right
    const riskColor = statusColor(COLORS, b.submission_risk);
    const badgeText = `${b.submission_risk.toUpperCase()} RISK`;
    const badgeW = doc.getTextWidth(badgeText) + 8;
    drawBadge(doc, ctx, badgeText, riskColor, margin + contentWidth - badgeW, ctx.yPos - 4.5);
    if (b.has_gating) {
      drawBadge(doc, ctx, 'GATING', COLORS.danger, margin + contentWidth - badgeW - 22, ctx.yPos - 4.5);
    }
    ctx.yPos += 5;

    // Combined name
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const cnLines = doc.splitTextToSize(b.combined_name || '', contentWidth - 4);
    cnLines.forEach(line => {
      ensureSpace(doc, ctx, 5);
      doc.text(line, margin + 2, ctx.yPos);
      ctx.yPos += 4;
    });
    ctx.yPos += 1;

    // Numbered steps
    if (b.numbered_minimum_path?.length > 0) {
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      b.numbered_minimum_path.forEach(s => {
        ensureSpace(doc, ctx, 6);
        doc.setFillColor(...COLORS.primary);
        doc.circle(margin + 4, ctx.yPos - 1.2, 2, 'F');
        doc.setTextColor(...COLORS.white);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(String(s.step), margin + 4, ctx.yPos + 0.4, { align: 'center' });
        doc.setTextColor(...COLORS.secondary);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(s.action || '', contentWidth - 14);
        lines.forEach((line, i) => {
          if (i > 0) ensureSpace(doc, ctx, 5);
          doc.text(line, margin + 9, ctx.yPos);
          ctx.yPos += 5;
        });
      });
    }
    ctx.yPos += 4;
  });
};

// ============================================================================
// 8. REMEDIATION ROADMAP
// ============================================================================
const renderRemediationRoadmap = (doc, ctx, data) => {
  const { COLORS, margin } = ctx;
  sectionHeader(doc, ctx, 'Path to DISP Readiness', 'Phased remediation roadmap grouped by workstream');

  const phases = [
    { key: 'phase_1', label: 'Phase 1', color: COLORS.danger },
    { key: 'phase_2', label: 'Phase 2', color: COLORS.warning },
    { key: 'phase_3', label: 'Phase 3', color: COLORS.success },
  ];

  phases.forEach(p => {
    const phase = data[p.key];
    if (!phase) return;
    ensureSpace(doc, ctx, 16);

    // Phase header
    doc.setFillColor(...p.color);
    doc.circle(margin + 3, ctx.yPos - 1, 2, 'F');
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`${p.label} - ${phase.timeline}`, margin + 8, ctx.yPos);
    ctx.yPos += 5;
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(phase.focus, margin + 8, ctx.yPos);
    ctx.yPos += 6;

    if (!phase.actions || phase.actions.length === 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...COLORS.secondary);
      doc.text('No actions required.', margin + 8, ctx.yPos);
      ctx.yPos += 6;
      ctx.yPos += 3;
      return;
    }

    // Group actions by workstream (domain / section_id)
    const groups = new Map();
    phase.actions.forEach(a => {
      const key = a.domain || 'general';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    });

    // Render each workstream as: heading + up to 5 outcome bullets
    Array.from(groups.entries()).forEach(([sectionId, actions]) => {
      ensureSpace(doc, ctx, 12);
      const workstreamLabel = domainLabelFromSectionId(sectionId);
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(9.5);
      doc.setFont('helvetica', 'bold');
      doc.text(workstreamLabel, margin + 8, ctx.yPos);
      ctx.yPos += 5;

      const shown = actions.slice(0, 5);
      shown.forEach(a => {
        const text = cleanActionText(a.action);
        writeBullet(doc, ctx, text, p.color);
      });
      if (actions.length > shown.length) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...COLORS.secondary);
        doc.text(`+ ${actions.length - shown.length} further items in this workstream`, margin + 12, ctx.yPos);
        ctx.yPos += 5;
      }
      ctx.yPos += 2;
    });
    ctx.yPos += 4;
  });
};

// ============================================================================
// 8.5 TECHNOLOGY -> CONTROL COVERAGE
// ============================================================================
const renderTechnologyControlCoverage = (doc, ctx, data) => {
  if (!data) return;
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Technology to Control Coverage', 'Platform footprint mapped to the controls each addresses');

  // Two-column platform list
  const colW = (contentWidth - 6) / 2;
  let colIndex = 0;
  let rowYStart = ctx.yPos;
  let rowMaxBottom = ctx.yPos;

  data.platforms.forEach((p, i) => {
    const lines = p.controls.map(c => `  - ${c}`);
    const required = lines.length * 4.5 + 10;
    if (colIndex === 0) {
      ensureSpace(doc, ctx, required);
      rowYStart = ctx.yPos;
      rowMaxBottom = ctx.yPos;
    }
    const x = margin + colIndex * (colW + 6);
    let y = rowYStart;
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, colW, required, 1.5, 1.5, 'S');
    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(p.label, x + 3, y + 5);
    doc.setTextColor(...COLORS.secondary);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    p.controls.forEach((c, j) => {
      doc.text(`- ${c}`, x + 3, y + 10 + j * 4.5);
    });
    rowMaxBottom = Math.max(rowMaxBottom, y + required);
    colIndex = (colIndex + 1) % 2;
    if (colIndex === 0 || i === data.platforms.length - 1) {
      ctx.yPos = rowMaxBottom + 3;
    }
  });
  if (colIndex !== 0) ctx.yPos = rowMaxBottom + 3;

  // Insight
  ensureSpace(doc, ctx, 28);
  const insightLines = doc.splitTextToSize(data.insight || '', contentWidth - 8);
  const boxH = insightLines.length * 4.5 + 10;
  doc.setFillColor(...COLORS.primary);
  doc.roundedRect(margin, ctx.yPos, contentWidth, boxH, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('TOOLING INSIGHT', margin + 4, ctx.yPos + 5);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  insightLines.forEach((line, i) => doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5));
  ctx.yPos += boxH + 6;
};

// ============================================================================
// 9. FINAL RECOMMENDATION (3-part)
// ============================================================================
const renderFinalRecommendation = (doc, ctx, data) => {
  const { COLORS, margin, contentWidth } = ctx;
  sectionHeader(doc, ctx, 'Final Recommendation', 'Closing position aligned to the Decision View');

  // Decision banner
  ensureSpace(doc, ctx, 16);
  const recColor = statusColor(COLORS, data.decision);
  doc.setFillColor(...recColor);
  doc.roundedRect(margin, ctx.yPos, contentWidth, 12, 2, 2, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(data.decision.toUpperCase(), margin + 5, ctx.yPos + 8);
  ctx.yPos += 16;

  writeParagraph(doc, ctx, data.reason, { bold: true, color: COLORS.primary, size: 10 });

  // 3-part structure: Current State -> Risk of Proceeding -> Next Step
  if (data.current_state || data.risk_of_proceeding || data.next_step) {
    const renderBlock = (label, body, bgColor, textColor) => {
      if (!body) return;
      const lines = doc.splitTextToSize(body, contentWidth - 10);
      const h = lines.length * 4.5 + 10;
      ensureSpace(doc, ctx, h + 4);
      doc.setFillColor(...bgColor);
      doc.roundedRect(margin, ctx.yPos, contentWidth, h, 2, 2, 'F');
      doc.setTextColor(...COLORS.secondary);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin + 4, ctx.yPos + 5);
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      lines.forEach((line, i) => doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5));
      ctx.yPos += h + 4;
    };
    renderBlock('CURRENT STATE', data.current_state, COLORS.light, COLORS.primary);
    renderBlock('RISK OF PROCEEDING NOW', data.risk_of_proceeding, COLORS.light, COLORS.primary);
    // Highlighted next step
    if (data.next_step) {
      const lines = doc.splitTextToSize(data.next_step, contentWidth - 10);
      const h = lines.length * 4.5 + 10;
      ensureSpace(doc, ctx, h + 4);
      doc.setFillColor(...COLORS.primary);
      doc.roundedRect(margin, ctx.yPos, contentWidth, h, 2, 2, 'F');
      doc.setTextColor(...COLORS.white);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('NEXT STEP', margin + 4, ctx.yPos + 5);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      lines.forEach((line, i) => doc.text(line, margin + 4, ctx.yPos + 10 + i * 4.5));
      ctx.yPos += h + 4;
    }
  } else {
    writeParagraph(doc, ctx, data.statement);
  }
};

// ============================================================================
// PUBLIC ENTRYPOINT
// ============================================================================
// `only` is an optional array of section keys to render (in the given order).
// Defaults to legacy full-order rendering. Use `only` to compose custom
// report structures without duplicating renderer logic.
const SECTION_RENDERERS = {
  exec_snapshot:   (doc, r, ctx) => renderExecutiveSnapshot(doc, ctx, r.executive_snapshot),
  commercial_impact: (doc, r, ctx) => renderCommercialImpact(doc, ctx, r.commercial_impact),
  how_to_read:     (doc, r, ctx) => renderHowToRead(doc, ctx, r.how_to_read),
  decision_view:   (doc, r, ctx) => renderDecisionView(doc, ctx, r.decision_view),
  domain_enhancements: (doc, r, ctx) => renderDomainEnhancements(doc, ctx, r.domain_enhancements),
  gating_analysis: (doc, r, ctx) => renderGatingAnalysis(doc, ctx, r.gating_analysis),
  evidence_readiness: (doc, r, ctx) => renderEvidenceReadiness(doc, ctx, r.evidence_readiness),
  csq_enhancements: (doc, r, ctx) => renderCsqEnhancements(doc, ctx, r.csq_enhancements),
  remediation_roadmap: (doc, r, ctx) => renderRemediationRoadmap(doc, ctx, r.remediation_roadmap),
  technology_control_coverage: (doc, r, ctx) => renderTechnologyControlCoverage(doc, ctx, r.technology_control_coverage),
  final_recommendation: (doc, r, ctx) => renderFinalRecommendation(doc, ctx, r.final_recommendation),
};

const DEFAULT_ORDER = [
  'exec_snapshot','commercial_impact','how_to_read','decision_view','domain_enhancements',
  'gating_analysis','evidence_readiness','csq_enhancements','remediation_roadmap',
  'technology_control_coverage','final_recommendation',
];

export const renderHybridSections = (doc, hybridReport, ctx, opts = {}) => {
  if (!hybridReport) return ctx.yPos;
  const order = Array.isArray(opts.only) && opts.only.length > 0 ? opts.only : DEFAULT_ORDER;
  const startsOnNewPage = opts.startOnNewPage !== false;

  if (startsOnNewPage) {
    doc.addPage();
    ctx.yPos = ctx.margin;
  }

  order.forEach(key => {
    const render = SECTION_RENDERERS[key];
    if (render) render(doc, hybridReport, ctx);
  });
  return ctx.yPos;
};

export default renderHybridSections;
